import { useEffect, useState } from "react";
import { Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { Icon } from "../../icons/Icon";
import { Brasao, Carregando } from "../../components/Layout";
import { api } from "../../lib/api";
import { Dashboard } from "./Dashboard";
import { FilaInscricoes } from "./FilaInscricoes";
import { DetalheInscricao } from "./DetalheInscricao";
import { Classificacao } from "./Classificacao";
import { Recursos } from "./Recursos";
import { Auditoria } from "./Auditoria";
import { ParametrosEdital } from "./ParametrosEdital";
import { Login } from "./Login";

export type Sessao = {
  autenticado: boolean; nome: string | null; email: string | null; perfil: string | null;
};

/** Perfis autorizados a decidir. LEITURA nunca aparece nessas listas. */
export const PODE_ANALISAR = ["ANALISTA", "PRESIDENTE", "ADMINISTRADOR"];
export const PODE_HOMOLOGAR = ["PRESIDENTE", "ADMINISTRADOR"];

export function Admin() {
  const [sessao, setSessao] = useState<Sessao | null>(null);
  const [menuAberto, setMenuAberto] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setMenuAberto(false); }, [pathname]);
  useEffect(() => {
    if (!menuAberto) return;
    const aoTeclar = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuAberto(false); };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [menuAberto]);

  async function carregar() {
    try {
      setSessao(await api.get<Sessao>("/api/admin/sessao"));
    } catch {
      setSessao({ autenticado: false, nome: null, email: null, perfil: null });
    }
  }

  useEffect(() => { void carregar(); }, []);

  useEffect(() => { document.title = "Painel da Comissão"; }, []);

  if (!sessao) return <Carregando texto="Verificando a sessão" />;
  if (!sessao.autenticado) return <Login aoEntrar={carregar} />;

  return (
    <div className="admin-casca">
      <aside className={`admin-lateral${menuAberto ? " aberto" : ""}`}>
        <div className="admin-marca">
          <Brasao tamanho={32} />
          <strong>Painel da Comissão</strong>
          <button type="button" className="btn btn-secundario admin-menu-btn"
                  aria-expanded={menuAberto} aria-controls="admin-menu"
                  aria-label={menuAberto ? "Fechar menu" : "Abrir menu"}
                  onClick={() => setMenuAberto((v) => !v)}>
            <Icon nome={menuAberto ? "fechar" : "menu"} tamanho={20} />
          </button>
        </div>

        <div id="admin-menu" className="admin-menu">
        <nav className="admin-nav" aria-label="Seções do painel">
          <NavLink to="/admin" end><Icon nome="planilha" tamanho={16} />Visão geral</NavLink>
          <NavLink to="/admin/inscricoes"><Icon nome="pessoas" tamanho={16} />Inscrições</NavLink>
          <NavLink to="/admin/classificacao"><Icon nome="lista" tamanho={16} />Classificação</NavLink>
          <NavLink to="/admin/recursos"><Icon nome="arquivo" tamanho={16} />Recursos</NavLink>
          <NavLink to="/admin/edital"><Icon nome="editar" tamanho={16} />Parâmetros</NavLink>
          <NavLink to="/admin/auditoria"><Icon nome="escudo" tamanho={16} />Auditoria</NavLink>
        </nav>

        <div style={{ marginTop: "auto", fontSize: "var(--text-xs)", color: "var(--color-sidebar-text-muted)" }}>
          <p style={{ color: "var(--color-text)" }}>{sessao.nome}</p>
          <p>{rotuloPerfil(sessao.perfil)}</p>
          <form method="post" action="/api/admin/logout" style={{ marginTop: "var(--space-3)" }}>
            <button type="submit" className="btn btn-sutil btn-pequeno" style={{ color: "var(--color-sidebar-text)" }}>
              <Icon nome="cadeado" tamanho={14} />
              Sair
            </button>
          </form>
        </div>
        </div>
      </aside>

      <div className="admin-conteudo">
        <Routes>
          <Route path="/admin" element={<Dashboard />} />
          <Route path="/admin/inscricoes" element={<FilaInscricoes sessao={sessao} />} />
          <Route path="/admin/inscricoes/:id" element={<DetalheInscricao sessao={sessao} />} />
          <Route path="/admin/classificacao" element={<Classificacao sessao={sessao} />} />
          <Route path="/admin/recursos" element={<Recursos sessao={sessao} />} />
          <Route path="/admin/edital" element={<ParametrosEdital sessao={sessao} />} />
          <Route path="/admin/auditoria" element={<Auditoria />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </div>
    </div>
  );
}

export function rotuloPerfil(perfil: string | null): string {
  switch (perfil) {
    case "ADMINISTRADOR": return "Administrador";
    case "PRESIDENTE": return "Presidente da Comissão";
    case "ANALISTA": return "Analista";
    case "LEITURA": return "Somente leitura";
    default: return "Perfil não identificado";
  }
}
